import { Dispatch, FC, MouseEvent, ReactElement, ReactNode, SetStateAction } from 'react';
import { clsx } from 'clsx';
import Box from '../Box';
import IconButton from '../IconButton';
import Cross from '@o2/icons/Cross';
import { buttonClasses } from '../Button';
import { LinkProps } from '../Link';
import { ProductCardSharedProps, useUtilityClasses } from './ProductCard';
import { styled } from '@mui/material/styles';
import { ChevronRight } from '@o2/icons';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system/createStyled';
import colors from '../theme/colors';

export type ProductCardBodyProps = {
  children: ReactNode;
  fullWidth?: boolean;
  hideIcon?: boolean;
  target?: LinkProps['target'];
  chip?: ReactNode;
  topContent?: ReactNode;
  bottomContent?: ReactNode;
  enableTopContentClick?: boolean;
  enableBottomContentClick?: boolean;
  onClose?: (e: MouseEvent) => void;
  setTopContentHover: Dispatch<SetStateAction<boolean>>;
  setBottomContentHover: Dispatch<SetStateAction<boolean>>;
} & ProductCardSharedProps;

interface StyledChevronProps {
  hidden?: boolean;
  size?: ProductCardSharedProps['size'];
}

const StyledChevron = styled(ChevronRight, {
  shouldForwardProp: (prop) => sysShouldForwardProp(prop) && prop !== 'hidden' && prop !== 'size',
})<StyledChevronProps>(({ hidden }) => ({
  ...(hidden && {
    visibility: 'hidden',
  }),
  color: colors.transparentGray[400],
}));

export const ProductCardBody: FC<ProductCardBodyProps> = ({
  children,
  endIcon,
  startIcon,
  hideIcon,
  linkless,
  size,
  chip,
  topContent,
  bottomContent,
  topContentColor,
  bottomContentColor,
  enableTopContentClick,
  enableBottomContentClick,
  onClose,
  setTopContentHover,
  setBottomContentHover,
}) => {
  const classes = useUtilityClasses({
    size,
    chip,
    startIcon,
    endIcon,
    hideIcon,
    topContentColor,
    bottomContentColor,
  });

  const endIconComponent: ReactElement =
    hideIcon || linkless ? (
      <StyledChevron hidden />
    ) : (
      endIcon || <StyledChevron className={clsx(classes.defaultChevron)} />
    );

  return (
    <>
      {chip && <Box className={clsx(classes.chipWrapper)}>{chip}</Box>}
      {onClose && (
        <IconButton
          variant="circle"
          color="primary"
          size="small"
          onClick={onClose}
          className={clsx(classes.closeButton)}
        >
          <Cross sx={{ fontSize: 2 }} />
        </IconButton>
      )}
      {topContent && (
        <Box
          className={clsx(classes.topContent)}
          onMouseOver={() => {
            !enableTopContentClick && setTopContentHover(true);
          }}
          onMouseOut={() => {
            !enableTopContentClick && setTopContentHover(false);
          }}
          onClick={(e: MouseEvent) => !enableTopContentClick && e.stopPropagation()}
        >
          {topContent}
        </Box>
      )}
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        flex={1}
        className={clsx(classes.mainContent)}
      >
        {startIcon && (
          <Box
            component="span"
            className={clsx(
              buttonClasses.startIcon,
              size === 'x-small' || size === 'small'
                ? buttonClasses.iconSizeSmall
                : size === 'medium'
                ? buttonClasses.iconSizeMedium
                : '',
              classes.startIcon,
              startIcon.props.className
            )}
          >
            {startIcon}
          </Box>
        )}
        <Box display="flex" flex={1}>
          {children}
        </Box>
        {endIconComponent && (
          <Box
            component="span"
            className={clsx(
              buttonClasses.endIcon,
              size === 'x-small' || size === 'small'
                ? buttonClasses.iconSizeSmall
                : size === 'medium'
                ? buttonClasses.iconSizeMedium
                : '',
              classes.endIcon,
              endIconComponent.props.className
            )}
          >
            {endIconComponent}
          </Box>
        )}
      </Box>
      {bottomContent && (
        <Box
          className={clsx(classes.bottomContent)}
          onMouseOver={() => {
            !enableBottomContentClick && setBottomContentHover(true);
          }}
          onMouseOut={() => {
            !enableBottomContentClick && setBottomContentHover(false);
          }}
          onClick={(e: MouseEvent) => !enableBottomContentClick && e.stopPropagation()}
        >
          {bottomContent}
        </Box>
      )}
    </>
  );
};
